import { createFileRoute, Link } from "@tanstack/react-router";
import { Award, Heart, ShieldCheck, Sparkles, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FadeUp } from "@/components/Motion";
import { buildMeta, buildLinks, breadcrumbSchema } from "@/lib/seo";
import { SITE, whatsappLink } from "@/lib/site";
import podologaImg from "@/assets/podologa-belem-para-rosa-chagas.jpg";
import recepcaoImg from "@/assets/clinica-podologia-belem-recepcao.jpg";

export const Route = createFileRoute("/sobre")({
  head: () => ({
    meta: buildMeta({
      title: "Sobre a Podomasso — Podóloga em Belém do Pará | Rosa Chagas CR8M 00017",
      description:
        "Conheça a Podomasso, clínica de podologia referência em Belém do Pará. A podóloga Rosa Chagas (CR8M 00017) cuida da saúde dos seus pés com técnica e carinho.",
      path: "/sobre",
    }),
    links: buildLinks("/sobre"),
    scripts: [
      breadcrumbSchema([
        { name: "Início", path: "/" },
        { name: "Sobre", path: "/sobre" },
      ]),
    ],
  }),
  component: SobrePage,
});

function SobrePage() {
  return (
    <>
      <section className="relative bg-gradient-soft py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeUp>
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-primary">
              Sobre a Podomasso
            </p>
            <h1 className="max-w-3xl font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              A clínica de podologia de referência em{" "}
              <span className="text-gradient-hero">Belém do Pará</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
              Cuidando dos pés dos paraenses com dedicação, técnica e biossegurança rigorosa há
              mais de uma década.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <FadeUp>
              <img
                src={podologaImg}
                alt="Rosa Chagas — Podóloga em Belém do Pará — CR8M 00017"
                loading="lazy"
                width={1200}
                height={1500}
                className="aspect-[4/5] w-full rounded-3xl object-cover shadow-elegant"
              />
            </FadeUp>
            <FadeUp delay={0.15}>
              <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-primary">
                Podóloga Responsável
              </p>
              <h2 className="font-display text-3xl font-bold sm:text-4xl">
                Rosa Chagas — CR8M 00017
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                Com mais de 10 anos de experiência em podologia clínica, Rosa Chagas é a podóloga
                responsável técnica da Podomasso. Especializada em tratamentos avançados, atende
                cada paciente com atenção individualizada, escuta ativa e protocolos de
                biossegurança rigorosos.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Sua missão é levar alívio, conforto e qualidade de vida — devolvendo aos seus pés a
                saúde que eles merecem.
              </p>

              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: Award,
                    title: "Formação Técnica em Podologia",
                    desc: "Registro CR8M 00017 — Conselho Regional",
                  },
                  {
                    icon: ShieldCheck,
                    title: "Biossegurança Hospitalar",
                    desc: "Esterilização rigorosa de todos os instrumentos",
                  },
                  {
                    icon: Sparkles,
                    title: "Equipamentos Modernos",
                    desc: "Tecnologia de última geração para procedimentos indolores",
                  },
                  {
                    icon: Heart,
                    title: "Atendimento Humanizado",
                    desc: "Escuta ativa, acolhimento e cuidado personalizado",
                  },
                ].map((d) => (
                  <div key={d.title} className="flex gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary">
                      <d.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{d.title}</h3>
                      <p className="text-sm text-muted-foreground">{d.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="bg-secondary/30 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <FadeUp>
              <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-primary">
                Nossa Clínica
              </p>
              <h2 className="font-display text-3xl font-bold sm:text-4xl">
                Um ambiente acolhedor para cuidar da saúde dos seus pés
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                A Podomasso foi pensada para receber você com conforto, privacidade e segurança.
                Nossas salas são projetadas para garantir o melhor atendimento podológico em Belém
                do Pará — com biossegurança hospitalar, equipamentos modernos e ambientação que
                relaxa.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> Esterilização hospitalar de todos os instrumentos
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> Equipamentos eletrônicos de podologia clínica
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> Ambiente climatizado e privativo
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> Atendimento {SITE.hours.days}, das {SITE.hours.open} às {SITE.hours.close}
                </li>
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild variant="hero" size="lg">
                  <Link to="/agendamento">Agendar avaliação</Link>
                </Button>
                <Button asChild variant="whatsapp" size="lg">
                  <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
                    Falar no WhatsApp
                  </a>
                </Button>
              </div>
            </FadeUp>
            <FadeUp delay={0.15}>
              <img
                src={recepcaoImg}
                alt="Recepção da Clínica Podomasso de Podologia em Belém do Pará"
                loading="lazy"
                width={1600}
                height={1067}
                className="aspect-[4/3] w-full rounded-3xl object-cover shadow-elegant"
              />
            </FadeUp>
          </div>
        </div>
      </section>
    </>
  );
}
